import { darken, lighten, Box } from '@mui/material';
import { grey } from '@mui/material/colors';

const PRIMARY = '#FA4B00';
const SECONDARY = '#212121';
const SUCCESS = '#4CAF50';
const ERROR = '#D32F2F';
const WARNING = '#FFA726';
const INFO = '#2196F3';
const TEXT = '#2B2B2B';
const DISABLED_TEXT = '#BCBCBC';
const ALT_TEXT = '#A2A2A2';
const BACKGROUND = '#F9FAFB';
const MAIN_HEADER_BG = '#FFF8F4';
const CHILD_HEADER_BG = '#F4F4F4';
const DISABLE_BG = '#F2F2F2';
const FIELD_BORDER = '#AFBACA';
const TABLE_BORDER = '#E2E2E2';
const ICON = '#546178';
const SUCCESS_TEXT = '#FFFFFF';

// New Color Palettes
const ORANGE = '#F2662E';
const ORANGE_25 = '#FEF0EA';
const ORANGE_100 = '#FBD1C0';
const ORANGE_200 = '#FAC2AB';
const ORANGE_400 = '#F58558';
const ORANGE_500 = '#F2662E';
const GREY_900 = '#1C222B';
const GREY_700 = '#3D4A5C';
const GREY_500 = '#566A81';
const GREY_600 = '#455468';
const GREY_400 = '#8897AE';
const GREY_300 = '#AFBACA';
const GREY_200 = '#D7DFE9';
const GREY_100 = '#E9EFF6';
const GREY_50 = '#F0F3F9';
const GREY_25 = '#F9FAFB';
const GREEN_600 = '#3D8C40';
const WHITE = '#FFFFFF';
const BLACK = '#000000';
const BLACK_50 = '#D3D3D3';
const BLACK_25 = '#E9E9E9';
const BLACK_400 = '#4D4D4D';
const BLACK_500 = '#212121';
const BLACK_600 = '#1A1A1A';
const BLACK_700 = '#141414';
const BLACK_900 = '#0A0A0A';
const BLUE_700 = '#234C72';
const RED = '#D32F2F';
const RED_LIGHT = '#ff9393';
const GREEN_400 = '#EDF7EE';
const GREEN_LIGHT = '#ceffd0';
const BG_LIGHT = '#FBEAEA';
const BG_HIGHLIGHT = '#FFEDD4';

declare module '@mui/material/styles' {
  interface PaletteColor {
    bgLight: string;
  }

  interface Palette {
    myAwesomeColor: string;
  }
  interface TypeText {
    success: string;
    dark: string;
  }
  interface SimplePaletteColorOptions {
    alert: string;
    bgLight: string;
  }
  interface TypeBackground {
    mainHeader: string;
    childHeader: string;
    dark: string;
    default: string;
    disabled: string;
  }
  interface DynamicValues {
    [name: string]: string | number;
  }
  export interface CustomColors {
    orange: string;
    orange_25: string;
    orange_100: string;
    orange_200: string;
    orange_400: string;
    orange_500: string;
    grey_900: string;
    grey_700: string;
    grey_600: string;
    grey_500: string;
    grey_400: string;
    grey_300: string;
    grey_200: string;
    grey_100: string;
    grey_50: string;
    grey_25: string;
    green_600: string;
    white: string;
    black: string;
    black_50: string;
    black_25: string;
    black_400: string;
    black_500: string;
    black_600: string;
    black_700: string;
    black_900: string;
    blue_700: string;
    red: string;
    red_light: string;
    success: string;
    green_400: string;
    green_light: string;
    bg_light: string;
    bg_highlight: string;
  }
  interface PaletteOptions {
    icon: DynamicValues;
    amend: DynamicValues;
    cancel: DynamicValues;
    colors: DynamicValues;
    animation: DynamicValues;
    border: DynamicValues;
  }

  interface BoxShadow {
    buttonShadow: string;
    modalShadow: string;
    dropDownListShadow: string;
  }
  interface Palette {
    icon: DynamicValues;
    amend: DynamicValues;
    cancel: DynamicValues;
    colors: CustomColors;
    animation: DynamicValues;
    border: DynamicValues;
    tertiary: PaletteColor;
    boxShadow: BoxShadow;
  }
}

const EchoPalette = {
  primary: {
    light: lighten(ORANGE, 0.4),
    main: ORANGE,
    dark: darken(ORANGE, 0.1),
    alert: '#d1eef8',
  },
  secondary: {
    light: lighten(SECONDARY, 0.6),
    main: SECONDARY,
    dark: darken(SECONDARY, 0.1),
  },
  tertiary: {
    light: lighten(ALT_TEXT, 0.6),
    main: ALT_TEXT,
    dark: darken(ALT_TEXT, 0.1),
  },
  success: {
    light: lighten(SUCCESS, 0.4),
    main: SUCCESS,
    dark: darken(SUCCESS, 0.1),
    alert: '#beede1',

    // Legacy
    primary: '#5cd3b4',
    hover: lighten('#5cd3b4', 0.2),
  },
  warning: {
    light: lighten(WARNING, 0.4),
    main: WARNING,
    dark: darken(WARNING, 0.1),
  },
  error: {
    light: lighten(ERROR, 0.4),
    bgLight: '#FBEAEA',
    main: ERROR,
    dark: darken(ERROR, 0.1),
    alert: '#ffe2e1',
  },
  info: {
    light: lighten(INFO, 0.7),
    main: INFO,
    dark: darken(INFO, 0.1),
  },
  text: {
    primary: TEXT,
    secondary: ALT_TEXT,
    disabled: DISABLED_TEXT,
    success: SUCCESS_TEXT,
    dark: darken(TEXT, 0.1),
  },
  background: {
    mainHeader: MAIN_HEADER_BG,
    childHeader: CHILD_HEADER_BG,
    dark: darken(BACKGROUND, 0.1),
    default: BACKGROUND,
    disabled: DISABLE_BG,
  },
  border: {
    light: lighten(FIELD_BORDER, 0.7),
    main: FIELD_BORDER,
    dark: darken(FIELD_BORDER, 0.1),
    secondary: TABLE_BORDER,
  },

  // Legacy
  animation: {
    transparent: 'rgba(0, 0, 0, 0)',
  },

  icon: {
    light: lighten(ICON, 0.7),
    main: ICON,
    dark: '#394457',
    opacityRegular: 0.5,
    opacityHover: 1,
  },

  amend: {
    main: '#d1eef8',
    dark: lighten('#d1eef8', 0.2),
  },
  cancel: {
    main: grey[800],
  },

  colors: {
    orange: ORANGE,
    orange_25: ORANGE_25,
    orange_100: ORANGE_100,
    orange_200: ORANGE_200,
    orange_400: ORANGE_400,
    orange_500: ORANGE_500,
    grey_900: GREY_900,
    grey_700: GREY_700,
    grey_500: GREY_500,
    grey_600: GREY_600,
    grey_400: GREY_400,
    grey_300: GREY_300,
    grey_200: GREY_200,
    grey_100: GREY_100,
    grey_50: GREY_50,
    grey_25: GREY_25,
    green_600: GREEN_600,
    white: WHITE,
    black: BLACK,
    black_50: BLACK_50,
    black_25: BLACK_25,
    black_400: BLACK_400,
    black_500: BLACK_500,
    black_600: BLACK_600,
    black_700: BLACK_700,
    black_900: BLACK_900,
    blue_700: BLUE_700,
    red: RED,
    red_light: RED_LIGHT,
    success: SUCCESS,
    disabled: DISABLED_TEXT,
    green_400: GREEN_400,
    green_light: GREEN_LIGHT,
    bg_light: BG_LIGHT,
    bg_highlight: BG_HIGHLIGHT,
  },
  boxShadow: {
    buttonShadow: `0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)`,
    modalShadow: `0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)`,
    dropDownListShadow: `0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.10)`,
  },
};

export default EchoPalette;
